'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { toDate } from './hybridActivityDates';

export const DATE_FROM_PARAM = 'activityFrom';
export const DATE_TO_PARAM = 'activityTo';

const isWholeExtent = (range, extent) =>
    range.from <= extent.min && range.to >= extent.max;

// A window read off a shared URL, clamped into what the data actually covers.
// Nothing usable in the querystring means the whole extent, which is the state
// the map opens in.
const readRange = (searchParams, extent) => {
    const from = toDate(searchParams?.get(DATE_FROM_PARAM));
    const to = toDate(searchParams?.get(DATE_TO_PARAM));

    // A window that is backwards, or that lies wholly outside the data, says
    // nothing this map can show. Better to open on the whole extent than on an
    // empty map a reader cannot explain.
    if (
        from === null ||
        to === null ||
        from > to ||
        to < extent.min ||
        from > extent.max
    ) {
        return null;
    }

    // A window reaching past both ends narrows nothing. Read as the whole
    // extent it would have the control reporting a filter, and offering to
    // clear one, over a map showing everything it has.
    if (isWholeExtent({ from, to }, extent)) {
        return null;
    }

    return {
        from: from < extent.min ? extent.min : from,
        to: to > extent.max ? extent.max : to,
    };
};

// How long a selection sits before it is written to the URL. Long enough that
// a slider drag — a selection per tick — lands as one write when the hand
// settles, short enough that a shared URL is never behind what is on screen by
// the time anyone could copy it.
export const URL_WRITE_DELAY = 300;

/**
 * The window of time the map is narrowed to, kept in the querystring.
 *
 * In the URL so a reader can share what they are looking at, the way the world
 * map shares its filters. Written on selection rather than from an effect on the
 * state, so the first render cannot overwrite the range the URL arrived with.
 *
 * The whole extent is expressed as no parameters at all: an untouched map should
 * hand out the URL it was opened with.
 *
 * Selections take effect immediately; the URL write trails them by a moment,
 * so a scrubbed slider — a selection per tick — writes once when the hand
 * settles. Written with history.replaceState rather than router.replace: the
 * page reads its searchParams on the server, so a router navigation re-renders
 * the whole page for a window only this hook reads — and the fresh props that
 * come back re-frame the map under the reader's hand. Next keeps
 * useSearchParams in step with native history writes, so the URL still reads
 * back the same way.
 */
export const useHybridActivityDateRange = (extent) => {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    // null until a reader narrows it or a URL says otherwise, which keeps "the
    // whole extent" from being a copy of the extent that could fall behind it.
    const [selected, setSelected] = useState(null);
    const hasReadUrl = useRef(false);

    // The one URL write in flight, if any. One rather than a queue: a newer
    // selection makes the pending write a URL nobody is looking at.
    const pendingWrite = useRef(null);

    // An unmounted map writes nothing: the pending URL described a view that
    // is no longer on screen.
    useEffect(() => () => clearTimeout(pendingWrite.current), []);

    useEffect(() => {
        // Only once, and only once the data has an extent to clamp into. A
        // second pass would fight the reader for the fields.
        if (hasReadUrl.current || !extent) {
            return;
        }

        hasReadUrl.current = true;
        setSelected(readRange(searchParams, extent));
    }, [extent, searchParams]);

    const selectRange = useCallback(
        (range) => {
            const isCleared = !range || !extent || isWholeExtent(range, extent);

            setSelected(isCleared ? null : range);

            const params = new URLSearchParams(searchParams?.toString() || '');

            if (isCleared) {
                params.delete(DATE_FROM_PARAM);
                params.delete(DATE_TO_PARAM);
            } else {
                params.set(DATE_FROM_PARAM, range.from);
                params.set(DATE_TO_PARAM, range.to);
            }

            const query = params.toString();

            // Whatever this selection asks of the URL replaces what the last
            // one asked — including asking nothing at all.
            clearTimeout(pendingWrite.current);

            // A write of the URL we are already on re-renders the map for
            // nothing, and re-renders that answer each other are how a loop
            // starts.
            if (query === (searchParams?.toString() || '')) {
                return;
            }

            pendingWrite.current = setTimeout(() => {
                window.history.replaceState(
                    null,
                    '',
                    query ? `${pathname}?${query}` : pathname
                );
            }, URL_WRITE_DELAY);
        },
        [extent, pathname, searchParams]
    );

    return {
        // What the control shows and the filter reads: the selection, or the
        // whole extent while there is none.
        range: selected || (extent && { from: extent.min, to: extent.max }),
        // What the filter applies. Null leaves every point in place, so a map a
        // reader has not narrowed is never filtered at all.
        appliedRange: selected,
        selectRange,
    };
};
